import { FunctionComponent, useContext, useEffect, useState } from 'react'
import { IPostAssumption } from '../../shared/services/assumption/assumption.model'
import { Box, Checkbox, Chip, FormControl, FormHelperText, InputLabel, ListItemText, MenuItem, OutlinedInput, Select, SelectChangeEvent, TextField } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import AssumptionService from '../../shared/services/assumption/assumption.service';
import GroupService from '../../shared/services/group/group.service';
import { IGroup } from '../../shared/services/group/group.model';
import SubsidiaryService from '../../shared/services/subsidiary/subsidiary.service';
import { ISubsidiary } from '../../shared/services/subsidiary/subsidiary.model';
import { getSelectableSubsidiaries } from '../../shared/utils/subsidiary-utils';
import { getSelectableGroups, groupOptionLabel } from '../../shared/utils/group-options';
import { UserDetailsContext } from '../../context/user-details/user-details';
import { ScreenLabelsContext } from '../../context/screen-labels/screen-labels';
import { DialogWrapper } from '../presentation-components/display-elements-components/dialog-wrapper';

// Mirror the server's limits (assumption.controller.js): the name is VARCHAR(255) and Additional
// Information is capped at 5,000 characters, with a counter once it gets close.
export const ASSUMPTION_NAME_MAX = 255
export const ASSUMPTION_ADDITIONAL_INFORMATION_MAX = 5000
const ADDITIONAL_INFORMATION_COUNTER_FROM = 4500

export const AddAssumptionDialog: FunctionComponent<{
  assumption: IPostAssumption,
  assumptionId?: string,
  open: boolean,
  successHandler: (values: IPostAssumption, resetForm: () => void, setSelectedLocationsId: React.Dispatch<React.SetStateAction<any>>,) => void,
  cancelHandler: () => void,
  isSubmitting: boolean,
  errorCode?: number,
  errorMessage?: string,
}> = ({
  assumption,
  assumptionId,
  open,
  successHandler,
  cancelHandler,
  isSubmitting,
  errorCode,
  errorMessage,
}) => {
    //Services
    const groupService = GroupService.getInstance();
    const assumptionService = new AssumptionService();
    const subsidiaryService = SubsidiaryService.getInstance();
    const userDetailsContext = useContext(UserDetailsContext)
    //Context
    const screenLabelsContext = useContext(ScreenLabelsContext)
    const organisationDetails = userDetailsContext.userDetails?.organisation
    //State
    const [subsidiaries, setSubsidiaries] = useState<ISubsidiary[]>()
    const [group, setGroup] = useState<IGroup>()
    // Without a group passed in (the overview page), the user picks one. Group stays required.
    const [allGroups, setAllGroups] = useState<IGroup[]>([])
    const chooseGroup = !assumption.groupId
    const [selectedSubsidiariesId, setSelectedSubsidiariesId] = useState<string[]>([])
    //Variables
    const status = [
      {
        title: 'Uncertain',
        description: 'no clear conclusion can be drawn; lacking sufficient information',
      },
      {
        title: 'Possible',
        description: 'could happen or be true, but with limited or inconclusive evidence',
      },
      {
        title: 'Probable',
        description: 'high likelihood, but not guaranteed; supported by strong evidence',
      },
      {
        title: 'Certain',
        description: 'full confidence, no doubt about the outcome or fact',
      }
    ]

    useEffect(() => {
      subsidiaryService.getAll()
        .then(response => {
          setSubsidiaries(response)
          if (response.length === 1) {
            setSelectedSubsidiariesId([response[0].id])
          }
        })
        .catch(err => {
          console.log(err)
        })

      groupService.getGroups()
        .then(response => {
          setGroup(response.find(group => group.id === assumption.groupId))
          setAllGroups(response)
        })
        .catch(err => console.log(err))

    }, [])

    useEffect(() => {
      if (assumptionId) {
        assumptionService.getOne(assumptionId)
          .then(response => {
            formik.setValues({
              name: response.name,
              description: response.description,
              status: response.status,
              groupId: response.groupId,
              additionalInformation: response.additionalInformation,
              subsidiaries: response.subsidiaries
            })
            setSelectedSubsidiariesId(response.subsidiaries)
          })
          .catch(err => console.log(err))
      }
    }, [assumptionId])


    useEffect(() => {
      formik.setFieldValue('subsidiaries', selectedSubsidiariesId);
    }, [selectedSubsidiariesId]);

    const assumptionSchema = Yup.object().shape({
      name: Yup.string().trim().required('Name is required').max(ASSUMPTION_NAME_MAX, `Name can be at most ${ASSUMPTION_NAME_MAX} characters`),
      description: Yup.string().required('Description is required'),
      status: Yup.string().required('Status is required'),
      subsidiaries: Yup.array().of(Yup.string().required(`${screenLabelsContext.getLabel('subsidiary', { plural: false })} is required`)).min(1, `At least one ${screenLabelsContext.getLabel('subsidiary', { plural: false })} must be selected`),
      groupId: Yup.string().required('Group is required'),
      additionalInformation: Yup.string().nullable().notRequired()
        .max(ASSUMPTION_ADDITIONAL_INFORMATION_MAX, `Additional information can be at most ${ASSUMPTION_ADDITIONAL_INFORMATION_MAX.toLocaleString('en-GB')} characters`)
    })

    const formik = useFormik({
      initialValues: {
        name: assumption.name,
        description: assumption.description,
        status: assumption.status,
        groupId: assumption.groupId,
        subsidiaries: assumption.subsidiaries,
        additionalInformation: assumption.additionalInformation,
      },
      validationSchema: assumptionSchema,
      onSubmit: (values) => {
        successHandler(values, formik.resetForm, setSelectedSubsidiariesId)
      }
    });

    const selectLocationHandler = (e: SelectChangeEvent<string[]>) => {
      const { value } = e.target;
      setSelectedSubsidiariesId(typeof value === 'string' ? value.split(',') : value,);
    }

    const selectStatusHandler = (e: SelectChangeEvent<string>) => {
      const { value } = e.target;
      formik.setFieldValue('status', value)
    }

    const selectGroupHandler = (e: SelectChangeEvent<string>) => {
      formik.setFieldValue('groupId', e.target.value)
    }

    const additionalInformationLength = (formik.values.additionalInformation ?? '').length

    // The groups the overview offers, plus the assumption's own group when editing one recorded on a
    // group that has since been switched off (shared with the overview's note Group list).
    const groupOptions = getSelectableGroups(allGroups, formik.values.groupId)

    const closeHandler = () => {
      formik.resetForm();
      setSelectedSubsidiariesId([])
      cancelHandler();
    }

    return (
      <DialogWrapper open={open} onClose={closeHandler} title={assumptionId ? 'Edit Assumption' : 'Add Assumption'} cancelHandler={cancelHandler} submitHandler={formik.submitForm} isSubmitting={isSubmitting}>
        {!chooseGroup && group &&
          <TextField id='group' name='group' label='Group' type='text' fullWidth value={group.title} disabled sx={{ mt: 2 }} InputLabelProps={{
            shrink: true,
          }} />}
        {chooseGroup &&
          <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
            <InputLabel htmlFor='groupId'>Group</InputLabel>
            <Select
              id='groupId' label='Group' name='groupId'
              value={groupOptions.some(option => option.id === formik.values.groupId) ? formik.values.groupId : ''}
              onChange={selectGroupHandler}
              error={formik.touched.groupId && Boolean(formik.errors.groupId)}
              sx={{ color: 'text.primary' }}>
              {groupOptions.map(option =>
                <MenuItem key={option.id} value={option.id}>{groupOptionLabel(option)}</MenuItem>
              )}
            </Select>
            {formik.touched.groupId && formik.errors.groupId &&
              <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.groupId}</FormHelperText>
            }
          </FormControl>}
        {errorCode === 5 && errorMessage &&
          <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{errorMessage}</FormHelperText>
        }
        <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
          <InputLabel htmlFor='name'>Name</InputLabel>
          <OutlinedInput id='name' label='Name' name='name' type='text'
            value={formik.values.name}
            onChange={formik.handleChange}
            error={formik.touched.name && Boolean(formik.errors.name)}
            sx={{ color: 'text.primary' }}></OutlinedInput>
          {formik.touched.name && formik.errors.name &&
            <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.name}</FormHelperText>
          }
          {(errorCode === 1 || errorCode === 7) && errorMessage &&
            <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{errorMessage}</FormHelperText>
          }
        </FormControl>
        <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
          <InputLabel htmlFor='description'>Description</InputLabel>
          <OutlinedInput id='description' label='Description' name='description' type='text'
            value={formik.values.description}
            onChange={formik.handleChange}
            error={formik.touched.description && Boolean(formik.errors.description)}
            sx={{ color: 'text.primary' }}></OutlinedInput>
          {formik.touched.description && formik.errors.description &&
            <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.description}</FormHelperText>
          }
          {errorCode === 3 && errorMessage &&
            <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{errorMessage}</FormHelperText>
          }
        </FormControl>
        {subsidiaries && (organisationDetails?.enableSubsidiaries || subsidiaries.length > 1) &&
          <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
            <InputLabel htmlFor='subsidiaries'>{screenLabelsContext.getLabel('subsidiary', { plural: true, capitalize: 'first' })}</InputLabel>
            <Select id='subsidiaries' name='subsidiaries' label={screenLabelsContext.getLabel('subsidiary', { plural: true, capitalize: 'first' })}
              value={selectedSubsidiariesId}
              onChange={selectLocationHandler}
              error={formik.touched.subsidiaries && Boolean(formik.errors.subsidiaries)}
              multiple
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value, index) => {
                    const selectedName = subsidiaries?.find(subsidiary => subsidiary.id === value)?.name
                    return (
                      <Chip key={index} label={selectedName} />
                    )
                  })}
                </Box>
              )} input={<OutlinedInput label={screenLabelsContext.getLabel('subsidiary', { plural: true, capitalize: 'first' })} />}>
              {getSelectableSubsidiaries(subsidiaries, selectedSubsidiariesId).sort((a, b) => a.name.localeCompare(b.name)).map((subsidiary, index) => (
                <MenuItem key={subsidiary.id} value={subsidiary.id}>
                  <Checkbox checked={selectedSubsidiariesId?.includes(subsidiary.id)} />
                  <ListItemText primary={subsidiary.name} />
                </MenuItem>
              ))}
            </Select>
            {formik.touched.subsidiaries && formik.errors.subsidiaries &&
              <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.subsidiaries}</FormHelperText>
            }
            {errorCode === 2 && errorMessage &&
              <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{errorMessage}</FormHelperText>
            }
          </FormControl>}
        <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
          <InputLabel htmlFor='status'> Status</InputLabel>
          <Select
            id='status' label='Status' name='status'
            value={formik.values.status || ''}
            onChange={selectStatusHandler}
            error={formik.touched.status && Boolean(formik.errors.status)}
            sx={{ color: 'text.primary', }}>
            {status.map((s, index) =>
              <MenuItem key={index} value={s.title}>{`${s.title} (${s.description})`}</MenuItem>
            )}
          </Select>
          {formik.touched.status && formik.errors.status &&
            <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.status}</FormHelperText>
          }
          {errorCode === 4 && errorMessage &&
            <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{errorMessage}</FormHelperText>
          }
        </FormControl>
        {/* Always shown and optional. It used to appear only for an "Other" status, which is not
            one of the options, so it could never be filled in. */}
        <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
          <InputLabel htmlFor='additionalInformation'>Additional Information (optional)</InputLabel>
          <OutlinedInput id='additionalInformation' label='Additional Information (optional)' name='additionalInformation' type='text'
            multiline
            minRows={2}
            value={formik.values.additionalInformation ?? ''}
            onChange={(event) => {
              formik.handleChange(event)
              formik.setFieldTouched('additionalInformation', true, false)
            }}
            error={formik.touched.additionalInformation && Boolean(formik.errors.additionalInformation)}
            sx={{ color: 'text.primary' }}></OutlinedInput>
          {formik.touched.additionalInformation && formik.errors.additionalInformation &&
            <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.additionalInformation}</FormHelperText>
          }
          {additionalInformationLength > ADDITIONAL_INFORMATION_COUNTER_FROM &&
            <FormHelperText
              error={additionalInformationLength > ASSUMPTION_ADDITIONAL_INFORMATION_MAX}
              sx={{ marginLeft: "16px !important" }}>
              {`${additionalInformationLength.toLocaleString('en-GB')} of ${ASSUMPTION_ADDITIONAL_INFORMATION_MAX.toLocaleString('en-GB')} characters`}
            </FormHelperText>
          }
          {errorCode === 8 && errorMessage &&
            <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{errorMessage}</FormHelperText>
          }
        </FormControl>
      </DialogWrapper>
    )
  }